import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { Button } from '@/components/ui/button';
import PIIFinderModule from '@/components/PIIFinderModule';
import { ArrowRight, ChevronRight, ShieldCheck } from 'lucide-react';
import {
  LOGO_URL, BRAND, HEADER, HERO, TRUST_BADGES, PROBLEM_SECTION,
  VALUE_SECTION, INDUSTRY_BLINDSPOTS_SECTION, PII_FINDER_SECTION,
  REPORT_SECTION, FRAMEWORK, FINAL_CTA, FOOTER,
} from '@/lib/content/landingContent';

// ──────────────────────────────────────────────────────────
// ANIMATION HELPERS
// ──────────────────────────────────────────────────────────

function FadeInSection({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration: 0.6, delay, ease: 'easeOut' }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// ──────────────────────────────────────────────────────────
// LANDING PAGE
// ──────────────────────────────────────────────────────────

const LandingPage = () => {
  const piiRef = useRef(null);

  const scrollToPII = () => {
    piiRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const HeroBadgeIcon = HERO.badge.icon;
  const FinalCtaIcon = FINAL_CTA.icon;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">

      {/* ═══════════════════ STICKY HEADER ═══════════════════ */}
      <header className="fixed top-0 w-full z-50 bg-white/90 backdrop-blur-md border-b border-slate-200" data-testid="sticky-header">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-3.5 flex items-center justify-between">
          <Link to="/" data-testid="header-logo" className="flex items-center gap-2">
            <img src={LOGO_URL} alt={BRAND.name} className="h-9" />
          </Link>
          <nav className="hidden md:flex items-center gap-8">
            {HEADER.navLinks.map((link) =>
              link.action === 'scroll-pii' ? (
                <button key={link.label} onClick={scrollToPII} className="text-sm font-semibold text-slate-600 hover:text-[#008080] transition-colors" data-testid={link.testId}>
                  {link.label}
                </button>
              ) : (
                <Link key={link.label} to={link.href} className="text-sm font-semibold text-slate-600 hover:text-[#008080] transition-colors" data-testid={link.testId}>
                  {link.label}
                </Link>
              )
            )}
            <Link to={HEADER.ctaDesktop.href}>
              <Button data-testid={HEADER.ctaDesktop.testId}
                className="bg-[#008080] hover:bg-[#006666] text-white rounded-sm px-6 py-2.5 font-bold text-sm shadow-md hover:shadow-lg transition-all hover:-translate-y-0.5">
                {HEADER.ctaDesktop.label}
                <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
            </Link>
          </nav>
          <div className="md:hidden flex items-center gap-3">
            <Link to="/login" className="text-sm font-semibold text-slate-600" data-testid="header-login-link-mobile">Login</Link>
            <Link to={HEADER.ctaMobile.href}>
              <Button className="bg-[#008080] hover:bg-[#006666] text-white rounded-sm px-4 py-2 font-bold text-xs" data-testid={HEADER.ctaMobile.testId}>
                {HEADER.ctaMobile.label}
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <div className="h-16" />

      {/* ═══════════════════ HERO SECTION ═══════════════════ */}
      <section className="relative overflow-hidden bg-[#003366] text-white" data-testid="hero-section">
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)',
          backgroundSize: '40px 40px'
        }} />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-20 lg:py-28">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
                <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-sm px-4 py-2 mb-6">
                  <HeroBadgeIcon className="h-4 w-4 text-[#008080]" />
                  <span className="text-xs font-bold uppercase tracking-widest text-blue-200">{HERO.badge.label}</span>
                </div>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.08] mb-6" data-testid="hero-headline">
                  {HERO.headline}
                </h1>
                <p className="text-base md:text-lg text-blue-200 mb-8 max-w-xl leading-relaxed font-medium" data-testid="hero-subheadline">
                  {HERO.subheadline}
                </p>
                <div className="flex flex-col sm:flex-row items-start gap-4">
                  <Button onClick={scrollToPII} data-testid={HERO.primaryCta.testId}
                    className="bg-[#008080] hover:bg-[#006666] text-white text-base px-8 py-6 rounded-sm font-bold shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5">
                    {HERO.primaryCta.label}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                  <Link to={HERO.secondaryCta.href}>
                    <Button variant="outline" data-testid={HERO.secondaryCta.testId}
                      className="border-2 border-white/30 text-white hover:bg-white/10 bg-transparent text-base px-8 py-6 rounded-sm font-bold">
                      {HERO.secondaryCta.label}
                    </Button>
                  </Link>
                </div>
              </motion.div>
            </div>
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.3 }} className="hidden lg:block">
              <div className="relative">
                <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-sm p-8">
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    {HERO.statsCards.map((stat, i) => (
                      <div key={i} className="bg-white/10 rounded-sm p-4 text-center">
                        <div className={`font-black ${stat.value.length > 5 ? 'text-2xl' : 'text-3xl'}`} style={{ color: stat.color }}>{stat.value}</div>
                        <div className="text-xs text-blue-200 font-semibold mt-1">{stat.label}</div>
                      </div>
                    ))}
                  </div>
                  <div className="space-y-3">
                    {HERO.progressBars.map((bar, i) => (
                      <div key={i} className="flex items-center justify-between">
                        <span className="text-sm text-blue-200 font-medium">{bar.label}</span>
                        <div className="w-32 bg-white/10 rounded-full h-2">
                          <div className="bg-[#008080] h-2 rounded-full" style={{ width: `${bar.pct}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══════════════════ TRUST STRIP ═══════════════════ */}
      <section className="bg-white border-b border-slate-200 py-5" data-testid="trust-strip">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-wrap justify-center gap-6 lg:gap-14">
            {TRUST_BADGES.map((badge) => {
              const Icon = badge.icon;
              return (
                <div key={badge.testId} className="flex items-center gap-2 text-[#003366]" data-testid={badge.testId}>
                  {badge.useLogo ? (
                    <img src={LOGO_URL} alt={BRAND.name} className="h-4" />
                  ) : (
                    <Icon className="h-4 w-4 text-[#008080]" />
                  )}
                  <span className="font-bold text-sm">{badge.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══════════════════ PROBLEM SECTION ═══════════════════ */}
      <section className="py-24 md:py-32 bg-[#F8FAFC]" data-testid="problem-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <FadeInSection>
            <div className="text-center mb-16">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#008080] mb-4 block">{PROBLEM_SECTION.badge}</span>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#003366] max-w-3xl mx-auto">
                {PROBLEM_SECTION.headline}
              </h2>
            </div>
          </FadeInSection>
          <div className="grid md:grid-cols-3 gap-8">
            {PROBLEM_SECTION.cards.map((card, i) => {
              const Icon = card.icon;
              return (
                <FadeInSection key={i} delay={i * 0.15}>
                  <div className="bg-white border border-slate-200 rounded-sm p-8 hover:shadow-lg transition-shadow h-full" data-testid={`problem-card-${i}`}>
                    <div className="p-3 bg-red-50 text-red-600 rounded-sm inline-block mb-5">
                      <Icon className="h-6 w-6" />
                    </div>
                    <h3 className="text-xl font-bold text-[#003366] mb-3 tracking-tight">{card.title}</h3>
                    <p className="text-slate-500 text-sm leading-relaxed font-medium">{card.description}</p>
                  </div>
                </FadeInSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══════════════════ VALUE PROPOSITION ═══════════════════ */}
      <section className="bg-[#003366] text-white py-24 md:py-32 relative overflow-hidden" data-testid="value-prop-section">
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.3) 1px, transparent 1px)',
          backgroundSize: '24px 24px'
        }} />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <FadeInSection>
            <div className="max-w-2xl mb-16">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#008080] mb-4 block">{VALUE_SECTION.badge}</span>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mb-4">{VALUE_SECTION.headline}</h2>
              <p className="text-blue-200 text-base font-medium leading-relaxed">{VALUE_SECTION.description}</p>
            </div>
          </FadeInSection>
          <div className="grid md:grid-cols-2 gap-6">
            {VALUE_SECTION.cards.map((prop, i) => {
              const Icon = prop.icon;
              return (
                <FadeInSection key={i} delay={i * 0.1}>
                  <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-sm p-6 hover:bg-white/10 transition-colors" data-testid={`value-prop-${i}`}>
                    <div className="flex items-start gap-4">
                      <div className="p-2.5 bg-[#008080] rounded-sm shrink-0">
                        <Icon className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-white mb-1.5">{prop.title}</h3>
                        <p className="text-blue-200 text-sm leading-relaxed">{prop.desc}</p>
                      </div>
                    </div>
                  </div>
                </FadeInSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══════════════════ INDUSTRY BLIND SPOTS ═══════════════════ */}
      <section className="py-20 md:py-24 bg-white" data-testid="industry-blind-spots-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <FadeInSection>
            <div className="text-center mb-12">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#008080] mb-4 block">{INDUSTRY_BLINDSPOTS_SECTION.badge}</span>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#003366] mb-3">{INDUSTRY_BLINDSPOTS_SECTION.headline}</h2>
              <p className="text-slate-500 text-base font-medium max-w-2xl mx-auto">{INDUSTRY_BLINDSPOTS_SECTION.description}</p>
            </div>
          </FadeInSection>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {INDUSTRY_BLINDSPOTS_SECTION.cards.map((card, i) => {
              const Icon = card.icon;
              return (
                <FadeInSection key={card.id} delay={i * 0.06}>
                  <button
                    onClick={() => { piiRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
                    className="w-full text-left bg-white border border-slate-200 rounded-sm p-5 hover:border-[#008080]/40 hover:shadow-md transition-all group"
                    data-testid={`industry-card-${card.id}`}>
                    <div className="flex items-start gap-3.5">
                      <div className="p-2 bg-[#003366]/5 rounded-sm shrink-0 group-hover:bg-[#008080]/10 transition-colors">
                        <Icon className="h-4 w-4 text-[#003366] group-hover:text-[#008080] transition-colors" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-bold text-[#003366] mb-0.5">{card.label}</h3>
                        <span className="text-[10px] font-bold text-[#008080] bg-[#008080]/10 px-1.5 py-0.5 rounded inline-block mb-2">{card.stat}</span>
                        <p className="text-[11px] text-slate-400 leading-relaxed">{card.description}</p>
                      </div>
                      <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-[#008080] shrink-0 mt-0.5 transition-colors" />
                    </div>
                  </button>
                </FadeInSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══════════════════ PII FINDER INTERACTIVE ═══════════════════ */}
      <section ref={piiRef} className="py-24 md:py-32 bg-[#F8FAFC] scroll-mt-20" data-testid="pii-finder-section" id="pii-finder">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <FadeInSection>
            <div className="text-center mb-12">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#008080] mb-4 block">{PII_FINDER_SECTION.badge}</span>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#003366] mb-3">{PII_FINDER_SECTION.headline}</h2>
              <p className="text-slate-500 text-base font-medium max-w-2xl mx-auto">{PII_FINDER_SECTION.description}</p>
            </div>
          </FadeInSection>
          <FadeInSection delay={0.2}>
            <div className="max-w-4xl mx-auto">
              <PIIFinderModule />
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* ═══════════════════ RESULTS PREVIEW / WHAT YOU GET ═══════════════════ */}
      <section className="py-24 md:py-32 bg-white" data-testid="results-preview-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <FadeInSection>
            <div className="text-center mb-16">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#008080] mb-4 block">{REPORT_SECTION.badge}</span>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#003366] mb-3">{REPORT_SECTION.headline}</h2>
              <p className="text-slate-500 text-base font-medium max-w-2xl mx-auto">{REPORT_SECTION.description}</p>
            </div>
          </FadeInSection>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {REPORT_SECTION.features.map((feat, i) => {
              const Icon = feat.icon;
              return (
                <FadeInSection key={i} delay={i * 0.1}>
                  <div className="flex items-start gap-4 p-6 bg-slate-50 border border-slate-200 rounded-sm hover:shadow-md transition-shadow" data-testid={`report-feature-${i}`}>
                    <div className="p-2.5 bg-[#008080]/10 text-[#008080] rounded-sm shrink-0">
                      <Icon className="h-5 w-5" />
                    </div>
                    <span className="font-bold text-[#003366] text-base">{feat.label}</span>
                  </div>
                </FadeInSection>
              );
            })}
          </div>
          <FadeInSection delay={0.3}>
            <div className="mt-20 grid md:grid-cols-3 gap-8">
              {REPORT_SECTION.steps.map((item, i) => (
                <div key={i} className="text-center" data-testid={`step-card-${i}`}>
                  <div className="text-5xl font-black text-[#008080]/15 mb-3">{item.step}</div>
                  <h3 className="text-lg font-bold text-[#003366] mb-2">{item.title}</h3>
                  <p className="text-slate-500 text-sm font-medium">{item.desc}</p>
                </div>
              ))}
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* ═══════════════════ TRUST: PILLARS & INDUSTRIES ═══════════════════ */}
      <section className="py-20 md:py-24 bg-slate-50 border-y border-slate-200" data-testid="trust-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <FadeInSection>
            <div className="mb-16">
              <div className="text-center mb-10">
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#008080] mb-3 block">{FRAMEWORK.badge}</span>
                <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#003366]">{FRAMEWORK.headline}</h2>
              </div>
              <div className="flex flex-wrap justify-center gap-3">
                {FRAMEWORK.pillars.map((pillar, i) => (
                  <div key={i} className="flex items-center gap-2 bg-white border border-slate-200 rounded-sm px-4 py-2.5 text-sm font-semibold text-[#003366] hover:border-[#008080] hover:shadow-sm transition-all" data-testid={`pillar-${i}`}>
                    <ShieldCheck className="h-3.5 w-3.5 text-[#008080]" />
                    {pillar}
                  </div>
                ))}
              </div>
            </div>
          </FadeInSection>
          <FadeInSection delay={0.2}>
            <div className="text-center">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-6 block">{FRAMEWORK.industryStripLabel}</span>
              <div className="flex flex-wrap justify-center gap-6">
                {FRAMEWORK.industryIcons.map((ind, i) => {
                  const Icon = ind.icon;
                  return (
                    <div key={i} className="flex flex-col items-center gap-2" data-testid={`industry-${i}`}>
                      <div className="p-3 bg-white border border-slate-200 rounded-sm">
                        <Icon className="h-5 w-5 text-[#003366]" />
                      </div>
                      <span className="text-xs font-bold text-slate-500">{ind.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* ═══════════════════ FINAL CTA ═══════════════════ */}
      <section className="bg-[#003366] text-white py-24 md:py-32" data-testid="final-cta-section">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <FadeInSection>
            <FinalCtaIcon className="h-10 w-10 text-[#008080] mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">{FINAL_CTA.headline}</h2>
            <p className="text-blue-200 text-base font-medium mb-10 max-w-xl mx-auto leading-relaxed">{FINAL_CTA.description}</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to={FINAL_CTA.primaryCta.href}>
                <Button data-testid={FINAL_CTA.primaryCta.testId}
                  className="bg-[#008080] hover:bg-[#006666] text-white text-base px-10 py-6 rounded-sm font-bold shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5">
                  {FINAL_CTA.primaryCta.label}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Button onClick={scrollToPII} variant="outline" data-testid={FINAL_CTA.secondaryCta.testId}
                className="border-2 border-white/30 text-white hover:bg-white/10 bg-transparent text-base px-10 py-6 rounded-sm font-bold">
                {FINAL_CTA.secondaryCta.label}
              </Button>
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* ═══════════════════ FOOTER ═══════════════════ */}
      <footer className="bg-[#002244] text-white py-10 border-t border-white/10" data-testid="footer">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-6">
              <img src="/codexero_logo_white.png" alt={BRAND.company} className="h-7" />
              <a href={BRAND.website} target="_blank" rel="noopener noreferrer"
                className="text-blue-300 hover:text-white transition-colors text-sm font-medium" data-testid="footer-website-link">
                www.{BRAND.domain}
              </a>
              <a href={`mailto:${BRAND.supportEmail}`}
                className="text-blue-300 hover:text-white transition-colors text-sm font-medium" data-testid="footer-email-link">
                {BRAND.supportEmail}
              </a>
            </div>
            <div className="flex items-center gap-6 text-blue-400 text-xs font-medium">
              {FOOTER.links.map((link, i) => (
                <a key={i} href={link.href} className="hover:text-white transition-colors">{link.label}</a>
              ))}
              <span>&copy; {BRAND.company} {new Date().getFullYear()}</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
